import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Chip,
  Switch,
  Slider,
  Rating,
  Badge,
  Avatar,
  IconButton,
  Fab,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  Favorite,
  Star,
  Settings,
  Add,
  NotificationsActive,
} from '@mui/icons-material';
import { ThemeSelector } from '../ThemeSelector/ThemeSelector';
import { styled } from '@mui/material/styles';
import { useTheme } from '../../theme/ThemeProvider';

const DemoContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  transition: 'all 0.3s ease-in-out',
}));

const NeumorphicCard = styled(Card)(({ theme }) => ({
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
}));

export interface ThemeDemoProps {
  /**
   * Whether to show the theme selector
   */
  showThemeSelector?: boolean;
}

export const ThemeDemo: React.FC<ThemeDemoProps> = ({
  showThemeSelector = true,
}) => {
  const { mode } = useTheme();
  const [sliderValue, setSliderValue] = React.useState<number>(30);
  const [switchValue, setSwitchValue] = React.useState<boolean>(true);
  const [ratingValue, setRatingValue] = React.useState<number | null>(4);

  const isNeumorphism = mode.includes('neumorphism');

  return (
    <DemoContainer>
      {showThemeSelector && (
        <Box mb={4}>
          <Typography variant="h4" gutterBottom>
            Design System Theme Demo
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            Switch between different themes to see how they affect the components.
            The neumorphism themes provide a soft, extruded plastic look with subtle shadows.
          </Typography>
          <Box mb={3}>
            <ThemeSelector variant="dropdown" />
          </Box>
          <Divider />
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Typography & Colors */}
        <Grid item xs={12} md={6}>
          <NeumorphicCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Typography & Colors
              </Typography>
              <Typography variant="h1" gutterBottom sx={{ fontSize: '2rem' }}>
                Heading 1
              </Typography>
              <Typography variant="h2" gutterBottom sx={{ fontSize: '1.75rem' }}>
                Heading 2
              </Typography>
              <Typography variant="body1" paragraph>
                This is body text that demonstrates how readable text appears in the current theme.
                {isNeumorphism && ' Notice the soft, muted colors in neumorphism mode.'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Secondary text with reduced opacity for hierarchy.
              </Typography>
            </CardContent>
          </NeumorphicCard>
        </Grid>

        {/* Interactive Elements */}
        <Grid item xs={12} md={6}>
          <NeumorphicCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Interactive Elements
              </Typography>
              <Box mb={2}>
                <TextField
                  label="Sample Input"
                  variant="outlined"
                  fullWidth
                  placeholder="Type something..."
                />
              </Box>
              <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                <Button variant="contained" color="primary">
                  Primary
                </Button>
                <Button variant="outlined" color="secondary">
                  Secondary
                </Button>
                <Button variant="text">
                  Text
                </Button>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body2">Switch:</Typography>
                <Switch
                  checked={switchValue}
                  onChange={(e) => setSwitchValue(e.target.checked)}
                />
              </Box>
            </CardContent>
          </NeumorphicCard>
        </Grid>

        {/* Chips & Badges */}
        <Grid item xs={12} md={6}>
          <NeumorphicCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Chips & Badges
              </Typography>
              <Box mb={2} display="flex" gap={1} flexWrap="wrap">
                <Chip label="Default" />
                <Chip label="Primary" color="primary" />
                <Chip label="Secondary" color="secondary" />
                <Chip label="Success" color="success" />
                <Chip label="Error" color="error" />
              </Box>
              <Box display="flex" gap={2} alignItems="center">
                <Badge badgeContent={4} color="primary">
                  <NotificationsActive />
                </Badge>
                <Badge badgeContent={99} color="secondary">
                  <Avatar sx={{ width: 32, height: 32 }}>U</Avatar>
                </Badge>
                <Badge variant="dot" color="error">
                  <IconButton>
                    <Settings />
                  </IconButton>
                </Badge>
              </Box>
            </CardContent>
          </NeumorphicCard>
        </Grid>

        {/* Sliders & Progress */}
        <Grid item xs={12} md={6}>
          <NeumorphicCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sliders & Progress
              </Typography>
              <Box mb={3}>
                <Typography variant="body2" gutterBottom>
                  Slider Value: {sliderValue}
                </Typography>
                <Slider
                  value={sliderValue}
                  onChange={(_, value) => setSliderValue(value as number)}
                  min={0}
                  max={100}
                  valueLabelDisplay="auto"
                />
              </Box>
              <Box mb={2}>
                <Typography variant="body2" gutterBottom>
                  Progress
                </Typography>
                <LinearProgress variant="determinate" value={sliderValue} />
              </Box>
              <Box>
                <Typography variant="body2" gutterBottom>
                  Rating
                </Typography>
                <Rating
                  value={ratingValue}
                  onChange={(_, value) => setRatingValue(value)}
                  icon={<Star />}
                  emptyIcon={<Star />}
                />
              </Box>
            </CardContent>
          </NeumorphicCard>
        </Grid>

        {/* Theme Showcase */}
        <Grid item xs={12}>
          <NeumorphicCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Theme: {mode.charAt(0).toUpperCase() + mode.slice(1).replace('-', ' ')}
              </Typography>
              <Typography variant="body1" paragraph>
                {isNeumorphism
                  ? `Neumorphism design creates a soft, tactile experience with subtle shadows and 
                     highlights. Elements appear to be gently extruded from the background surface, 
                     providing depth without harsh contrasts.`
                  : `Classic ${mode} theme with traditional Material Design principles. Clean, 
                     modern interface with clear visual hierarchy.`}
              </Typography>
              
              {isNeumorphism && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Neumorphism Features:
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    <li>Soft, dual-tone shadows for depth</li>
                    <li>Rounded corners for organic feel</li>
                    <li>Subtle color gradients</li>
                    <li>Tactile, touchable appearance</li>
                    <li>Minimal contrast for eye comfort</li>
                  </ul>
                </Box>
              )}
            </CardContent>
          </NeumorphicCard>
        </Grid>
      </Grid>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
      >
        <Add />
      </Fab>
    </DemoContainer>
  );
}; 